import {
  faBoxOpen,
  faFire,
  faLayerGroup,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dialog, RadioGroup, Transition } from '@headlessui/react'
import PropTypes from 'prop-types'
import { Fragment, useEffect, useState } from 'react'

import { useDebouncedValue } from '../../hooks/useDebounceValue'
import { useServer } from '../../hooks/useServer'
import {
  getMeasureSpanish,
  getProductTypeIcon,
  getTypeProductSpanish,
  printPrice,
} from '../../utils/functions'
import { getColorProductType } from '../../utils/tailwindcss'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function SearchProducts({
  onSelectProduct,
  onClose,
  options = {},
}) {
  const { isLoading, products, findAllProducts } = useServer({
    startLoading: false,
  })

  const [valueSearch, setValueSearch] = useState('')
  const debouncedValue = useDebouncedValue(valueSearch, 700)

  useEffect(() => {
    if (debouncedValue.length > 2) {
      findAllProducts({
        search: debouncedValue,
        ...options,
      })
    }
  }, [debouncedValue, options, findAllProducts])

  const [selected, setSelected] = useState()

  const onClicHandler = () => {
    if (selected) {
      const product = products.find((item) => item.id === selected)
      onSelectProduct(product)
      onClose()
    }
  }

  return (
    <>
      <Transition.Root as={Fragment} show={true}>
        <Dialog as="div" className="relative z-40" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className={
                    'relative w-2/4 transform overflow-hidden rounded-lg bg-white ' +
                    'text-left shadow-xl transition-all'
                  }
                >
                  <div className="h-96 bg-white px-4 pb-4 pt-2 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="w-full">
                        <div className="mb-2 shadow-sm">
                          <div className="pointer-events-none relative inset-y-9 left-0 ml-5 flex items-center">
                            <svg
                              className="h-5 w-5 text-gray-500 dark:text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                              ></path>
                            </svg>
                          </div>
                          <input
                            className={[
                              'block w-full rounded-lg border border-gray-300 bg-gray-50 p-4 pl-10 text-sm',
                              'text-gray-900 focus:border-gray-500 focus:ring-gray-500 dark:border-gray-600',
                              'dark:bg-gray-700 dark:text-white dark:placeholder-gray-400',
                              'dark:focus:border-gray-500 dark:focus:ring-gray-500',
                            ].join(' ')}
                            id="default-search"
                            onChange={(e) => setValueSearch(e.target.value)}
                            placeholder="Introduzca un criterio de búsqueda... "
                            type="search"
                          />
                        </div>

                        {!isLoading && products.length === 0 && (
                          <div className="text-center">
                            <FontAwesomeIcon
                              aria-hidden="true"
                              className="mt-3 h-16 w-16 text-gray-500"
                              icon={faBoxOpen}
                            />
                            <h3 className="mt-2 text-sm font-medium text-gray-500">
                              No hay productos que mostrar
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Introduzca un criterio de búsqueda
                            </p>
                          </div>
                        )}

                        {isLoading && (
                          <div className="mt-5 text-center">
                            <FontAwesomeIcon
                              aria-hidden="true"
                              className="h-10 w-10 animate-spin text-orange-500"
                              icon={faSpinner}
                            />
                            <h3 className="mt-3 text-sm font-medium text-gray-500">
                              Buscando...
                            </h3>
                          </div>
                        )}

                        {!isLoading && products.length !== 0 && (
                          <div
                            className={
                              'scroll scrollbar-hide mt-3 h-64 overflow-y-scroll scroll-smooth'
                            }
                          >
                            <RadioGroup onChange={setSelected} value={selected}>
                              <RadioGroup.Label className="sr-only">
                                {' '}
                                Pricing plans{' '}
                              </RadioGroup.Label>
                              <div className="relative -space-y-px rounded-md bg-white">
                                {products.map((product, index) => (
                                  <RadioGroup.Option
                                    className={({ checked }) =>
                                      classNames(
                                        index === 0
                                          ? 'rounded-tl-md rounded-tr-md'
                                          : '',
                                        index === products.length - 1
                                          ? 'rounded-bl-md rounded-br-md'
                                          : '',
                                        checked
                                          ? 'z-40 border-indigo-200 bg-indigo-50'
                                          : 'border-gray-200',
                                        'relative flex cursor-pointer flex-col border p-4 focus:outline-none ' +
                                          'md:grid md:grid-cols-4 md:pl-4 md:pr-6'
                                      )
                                    }
                                    key={index}
                                    value={product.id}
                                  >
                                    {({ active, checked }) => (
                                      <>
                                        <span className="flex items-center text-sm">
                                          <span
                                            aria-hidden="true"
                                            className={classNames(
                                              checked
                                                ? 'border-transparent bg-indigo-600'
                                                : 'border-gray-300 bg-white',
                                              active
                                                ? 'ring-2 ring-indigo-500 ring-offset-2'
                                                : '',
                                              'flex h-4 w-4 items-center justify-center rounded-full border'
                                            )}
                                          >
                                            <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                          </span>
                                          <RadioGroup.Label
                                            as="span"
                                            className={classNames(
                                              checked
                                                ? 'text-indigo-900'
                                                : 'text-gray-900',
                                              'ml-3 font-medium'
                                            )}
                                          >
                                            {product.name}
                                          </RadioGroup.Label>
                                        </span>
                                        <RadioGroup.Description
                                          as="span"
                                          className={classNames(
                                            checked
                                              ? 'text-indigo-700'
                                              : 'text-gray-500',
                                            'ml-6 pl-1 text-sm md:ml-0 md:pl-0 md:text-left'
                                          )}
                                        >
                                          <div
                                            className={classNames(
                                              getColorProductType(product.type),
                                              'ml-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-sm ' +
                                                'font-medium md:mt-2 lg:mt-0'
                                            )}
                                          >
                                            {product.type ===
                                              'READYFORSALE' && (
                                              <FontAwesomeIcon
                                                className={
                                                  'mr-2 align-middle text-green-500'
                                                }
                                                icon={
                                                  product.type === 'MENU'
                                                    ? faFire
                                                    : faLayerGroup
                                                }
                                              />
                                            )}
                                            <FontAwesomeIcon
                                              className={classNames(
                                                getColorProductType(
                                                  product.type
                                                ),
                                                'mr-2 align-middle'
                                              )}
                                              icon={getProductTypeIcon(
                                                product.type
                                              )}
                                            />
                                            {getTypeProductSpanish(
                                              product.type
                                            )}
                                          </div>
                                        </RadioGroup.Description>
                                        <RadioGroup.Description
                                          as="span"
                                          className="ml-6 pl-1 text-sm md:ml-0 md:pl-0 md:text-center"
                                        >
                                          <span
                                            className={classNames(
                                              checked
                                                ? 'text-indigo-900'
                                                : 'text-gray-900',
                                              'font-medium'
                                            )}
                                          >
                                            ${printPrice(product.averageCost)}
                                          </span>
                                        </RadioGroup.Description>
                                        <RadioGroup.Description
                                          as="span"
                                          className={classNames(
                                            checked
                                              ? 'text-indigo-700'
                                              : 'text-gray-500',
                                            'ml-6 pl-1 text-sm md:ml-0 md:pl-0 md:text-right'
                                          )}
                                        >
                                          {getMeasureSpanish(product.measure)}
                                        </RadioGroup.Description>
                                      </>
                                    )}
                                  </RadioGroup.Option>
                                ))}
                              </div>
                            </RadioGroup>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      className={[
                        'inline-flex w-full justify-center rounded-md border border-transparent bg-orange-500 px-4',
                        'py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2',
                        'focus:ring-red-500 focus:ring-offset-2 hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm',
                      ].join(' ')}
                      onClick={onClicHandler}
                      type="button"
                    >
                      Aceptar
                    </button>
                    <button
                      className={[
                        'mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4',
                        'py-2 text-base font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2',
                        'focus:ring-indigo-500 focus:ring-offset-2 hover:bg-gray-50 sm:ml-3 sm:mt-0',
                        'sm:w-auto sm:text-sm',
                      ].join(' ')}
                      onClick={onClose}
                      type="button"
                    >
                      Cancelar
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}

SearchProducts.propTypes = {
  onSelectProduct: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  options: PropTypes.object,
}
